import { supabase } from './supabaseClient';

export interface ProductSale {
  id: string;
  business_id: string;
  customer_id?: string;
  sale_number: string;
  subtotal_amount: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  discount_amount?: number;
  discount_details?: Array<{
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }>;
  original_subtotal?: number;
  payment_status: 'paid' | 'unpaid' | 'partial';
  payment_method?: string;
  payment_date?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
}

export interface ProductSaleItem {
  id: string;
  sale_id: string;
  stock_item_id: string;
  product_name: string;
  quantity: number;
  unit_price: number;
  tax_exempt: boolean;
  tax_rate: number;
  tax_amount: number;
  total_price: number;
  created_at: string;
}

export interface ProductSaleWithDetails extends ProductSale {
  customer_first_name?: string;
  customer_last_name?: string;
  customer_email?: string;
  customer_phone?: string;
  created_by_email?: string;
  item_count: number;
  total_items_sold: number;
  items?: ProductSaleItem[];
}

export interface CreateProductSaleData {
  business_id: string;
  customer_id?: string;
  subtotal_amount: number;
  tax_rate: number;
  tax_amount: number;
  total_amount: number;
  discount_amount?: number;
  discount_details?: Array<{
    id: string;
    title: string;
    type: 'percentage' | 'fixed';
    amount: number;
  }>;
  original_subtotal?: number;
  payment_status?: 'paid' | 'unpaid' | 'partial';
  payment_method?: string;
  payment_date?: string;
  notes?: string;
  items: {
    stock_item_id: string;
    product_name: string;
    quantity: number;
    unit_price: number;
    tax_exempt: boolean;
    tax_rate: number;
    tax_amount: number;
    total_price: number;
  }[];
}

// Calculate tax for product sales
export function calculateProductSaleTax(items: { price: number; quantity: number; tax_exempt?: boolean }[], taxRate: number = 5) {
  let subtotal = 0;
  let totalTax = 0;
  
  const itemsWithTax = items.map(item => {
    const itemSubtotal = item.price * item.quantity;
    const isTaxExempt = item.tax_exempt || false;
    const itemTaxRate = isTaxExempt ? 0 : taxRate;
    const itemTaxAmount = isTaxExempt ? 0 : Math.round((itemSubtotal * itemTaxRate / 100) * 100) / 100;
    const itemTotal = itemSubtotal + itemTaxAmount;
    
    subtotal += itemSubtotal;
    totalTax += itemTaxAmount;
    
    return {
      ...item,
      tax_exempt: isTaxExempt,
      tax_rate: itemTaxRate,
      tax_amount: itemTaxAmount,
      total_price: itemTotal
    };
  });
  
  const total = subtotal + totalTax;
  
  return {
    subtotal,
    tax_rate: taxRate,
    tax_amount: totalTax,
    total: total,
    items: itemsWithTax
  };
}

// Generate sale number
export async function generateSaleNumber(businessId: string): Promise<string> {
  const { data, error } = await supabase.rpc('generate_sale_number', {
    business_uuid: businessId
  });

  if (error) throw error;
  return data;
}

// Create a new product sale
export async function createProductSale(saleData: CreateProductSaleData): Promise<ProductSale> {
  try {
    // Generate sale number
    const saleNumber = await generateSaleNumber(saleData.business_id);

    // Create the main sale record
    const { data: sale, error: saleError } = await supabase
      .from('product_sales')
      .insert({
        business_id: saleData.business_id,
        customer_id: saleData.customer_id,
        sale_number: saleNumber,
        subtotal_amount: saleData.subtotal_amount,
        tax_rate: saleData.tax_rate,
        tax_amount: saleData.tax_amount,
        total_amount: saleData.total_amount,
        discount_amount: saleData.discount_amount || 0,
        discount_details: saleData.discount_details || [],
        original_subtotal: saleData.original_subtotal,
        payment_status: saleData.payment_status || 'unpaid',
        payment_method: saleData.payment_method,
        payment_date: saleData.payment_date,
        notes: saleData.notes,
        created_by: (await supabase.auth.getUser()).data.user?.id
      })
      .select()
      .single();

    if (saleError) throw saleError;

    // Create sale items
    const saleItems = saleData.items.map(item => ({
      sale_id: sale.id,
      stock_item_id: item.stock_item_id,
      product_name: item.product_name,
      quantity: item.quantity,
      unit_price: item.unit_price,
      tax_exempt: item.tax_exempt,
      tax_rate: item.tax_rate,
      tax_amount: item.tax_amount,
      total_price: item.total_price
    }));

    const { error: itemsError } = await supabase
      .from('product_sale_items')
      .insert(saleItems);

    if (itemsError) throw itemsError;

    // Always update stock quantities when creating a sale (inventory is committed)
    await updateStockQuantities(saleData.items, sale.id);

    return sale;
  } catch (error) {
    console.error('Error creating product sale:', error);
    throw error;
  }
}

// Update stock quantities after sale by creating stock transactions
async function updateStockQuantities(items: CreateProductSaleData['items'], productSaleId?: string) {
  for (const item of items) {
    // Create a stock transaction which will trigger the stock quantity update
    const { error } = await supabase
      .from('stock_transactions')
      .insert({
        item_id: item.stock_item_id,
        type: 'sale',
        quantity: item.quantity,
        price: item.unit_price, // Add the required price field
        notes: `Product sale - ${item.product_name}`,
        product_sale_id: productSaleId, // Link to the product sale
        created_at: new Date().toISOString()
      });

    if (error) {
      console.error(`Error creating stock transaction for item ${item.stock_item_id}:`, error);
      throw error;
    }
  }
}

// Get product sales for a business
export async function getProductSales(businessId: string): Promise<ProductSaleWithDetails[]> {
  const { data, error } = await supabase
    .from('product_sales_with_details')
    .select('*')
    .eq('business_id', businessId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

// Get a single product sale with items
export async function getProductSaleById(saleId: string): Promise<ProductSaleWithDetails | null> {
  // Get the main sale record
  const { data: sale, error: saleError } = await supabase
    .from('product_sales_with_details')
    .select('*')
    .eq('id', saleId)
    .single();

  if (saleError) throw saleError;

  // Get the sale items
  const { data: items, error: itemsError } = await supabase
    .from('product_sale_items')
    .select('*')
    .eq('sale_id', saleId)
    .order('created_at', { ascending: true });

  if (itemsError) throw itemsError;

  return {
    ...sale,
    items: items || []
  };
}

// Update payment status
export async function updatePaymentStatus(
  saleId: string,
  paymentStatus: 'paid' | 'unpaid' | 'partial',
  paymentMethod?: string,
  paymentDate?: string
): Promise<void> {
  const updateData: any = {
    payment_status: paymentStatus
  };

  if (paymentMethod) updateData.payment_method = paymentMethod;
  if (paymentDate) updateData.payment_date = paymentDate;

  const { error } = await supabase
    .from('product_sales')
    .update(updateData)
    .eq('id', saleId);

  if (error) throw error;

  // If marking as paid, update stock quantities
  if (paymentStatus === 'paid') {
    const sale = await getProductSaleById(saleId);
    if (sale?.items) {
      await updateStockQuantities(sale.items.map(item => ({
        stock_item_id: item.stock_item_id,
        product_name: item.product_name,
        quantity: item.quantity,
        unit_price: item.unit_price,
        tax_exempt: false, // Default value
        tax_rate: 0, // Default value
        tax_amount: 0, // Default value
        total_price: item.total_price
      })), saleId);
    }
  }
}

// Delete a product sale
export async function deleteProductSale(saleId: string): Promise<void> {
  const { error } = await supabase
    .from('product_sales')
    .delete()
    .eq('id', saleId);

  if (error) throw error;
}
